<template>
  <EdroneModal
    v-if="!!details"
    :open="!state.hasTag('closed')"
    :width="720"
    unique-id="d013e107-5069-474a-a417-8db451f7d7f8"
    @close="handleClose(state.can('CLOSE'))"
  >
    <template #title>
      {{
        details.type.charAt(0) +
        details.type.slice(1).replaceAll("_", " ").toLowerCase() +
        (details.orderData ? " #" + details.orderData.orderId : "")
      }}
    </template>
    <div
      v-if="state.hasTag('displayDetails') && details.products"
      :class="[$options.name, 'tw:flex flex-column tw:gap-6']"
    >
      <div :class="[$options.name + '__container', 'tw:pt-4 tw:flex tw:gap-6']">
        <div :class="[$options.name + '__container-details', 'tw:p-4']">
          <div :class="[$options.name + '_info-text', 'tw:py-2']">
            <p class="tw:caption" style="color: #545454">
              {{ trans._inbox.customer.activity.preview.product.name }}
            </p>
            <p class="tw:m-0">{{ details.products[currentPage - 1].title }}</p>
          </div>
          <hr class="tw:m-0" />
          <div :class="[$options.name + '_info-text', 'tw:py-2']">
            <p class="tw:caption" style="color: #545454">
              {{ trans._inbox.customer.activity.preview.product.amount }}
            </p>
            <p class="tw:m-0">{{ details.products[currentPage - 1].amount }}</p>
          </div>
          <hr class="tw:m-0" />
          <div :class="[$options.name + '_info-text', 'tw:py-2']">
            <p class="tw:caption" style="color: #545454">
              {{ trans._inbox.customer.activity.preview.product.price }}
            </p>
            <p class="tw:m-0">{{ getProductPrice(details.products[currentPage - 1].price) }}</p>
          </div>
          <hr class="tw:m-0" />
          <div :class="[$options.name + '_info-text', 'tw:py-2']">
            <p class="tw:caption" style="color: #545454">
              {{ trans._inbox.customer.activity.preview.product.id }}
            </p>
            <p class="tw:m-0">{{ details.products[currentPage - 1].productId }}</p>
          </div>
          <hr class="tw:m-0" />
          <div :class="[$options.name + '_info-text', 'tw:py-2']">
            <p class="tw:caption" style="color: #545454">
              {{ trans._inbox.customer.activity.preview.product.link }}
            </p>
            <p class="tw:m-0" style="word-break: break-word">
              <a :href="details.products ? details.products[currentPage - 1].url : ''" target="_blank">
                {{ details.products[currentPage - 1].url }}
              </a>
            </p>
          </div>
          <hr class="tw:m-0" />
          <div v-if="details.products && details.products[currentPage - 1].description" class="tw:pt-3">
            <p class="tw:body-2" style="color: #545454">
              {{ details.products[currentPage - 1].description }}
            </p>
          </div>
        </div>
        <div :class="[$options.name + '__container-image', 'tw:flex tw:justify-center tw:items-center tw:p-4']">
          <img
            v-if="details.products && details.products[currentPage - 1].image"
            :src="details.products[currentPage - 1].image || ''"
            alt="Product image"
            style="max-height: 100%; max-width: 100%"
          />
          <EdroneBadge v-else :label="trans._inbox.customer.activity.preview.product.image" size="small" />
        </div>
      </div>
      <div :class="[$options.name + '__navigation', 'tw:flex tw:items-center tw:gap-1']">
        <EdroneButtonIcon
          :disabled="currentPage <= 1"
          type="plain"
          unique-id="110fe43a-92bf-47df-912f-c00cc9d3f1df"
          @click="send({ type: 'CHANGE_PAGE', page: currentPage - 1 })"
        >
          <font-awesome-icon icon="fa-light fa-chevron-left" />
        </EdroneButtonIcon>
        <span class="tw:body-1 tw:px-4 tw:py-2">{{ currentPage + "/" + pages }}</span>
        <EdroneButtonIcon
          :disabled="currentPage >= pages"
          type="plain"
          unique-id="6b5213aa-b8cb-4a88-836c-fdb70d3362c9"
          @click="send({ type: 'CHANGE_PAGE', page: currentPage + 1 })"
        >
          <font-awesome-icon icon="fa-light fa-chevron-right" />
        </EdroneButtonIcon>
      </div>
    </div>
  </EdroneModal>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useActivityPreview } from "../machines/machineCustomerPreviewActivityPreview";
import { useCustomerPreviewActor } from "vue-app/pages/machines/machineCustomerPreview";
import EdroneModal from "edrone-components/EdroneModal.vue";
import EdroneButtonIcon from "edrone-components/EdroneButtonIcon.vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";

export default defineComponent({
  name: "CustomerPreviewActivityPreviewProductModal",
  components: {
    EdroneModal,
    EdroneButtonIcon,
    EdroneBadge,
  },
  emits: ["close"],
  setup(props, context) {
    const isDev = window.EDRONE_CONTEXT.user.isDev;

    const customerPreviewActor = useCustomerPreviewActor();
    const { state, send } = useActivityPreview(customerPreviewActor);

    const pages = computed(() => {
      return state.value.context.pages || 1;
    });
    const currentPage = computed(() => {
      return state.value.context.currentPage || 1;
    });
    const details = computed(() => {
      return state.value.context.details;
    });

    const handleClose = (canClose: boolean) => {
      if (!canClose) return;
      context.emit("close");
      send("CLOSE");
    };

    const getProductPrice = (productPrice: number) => {
      if (!productPrice) {
        return "N/A";
      }
      return details.value?.orderData ? productPrice + details.value.orderData.orderCurrency : productPrice;
    };

    return {
      state,
      send,
      isDev,
      pages,
      currentPage,
      details,
      handleClose,
      getProductPrice,
    };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.CustomerPreviewActivityPreviewProductModal {
  height: 500px;
}

.CustomerPreviewActivityPreviewProductModal__container {
  height: calc(100% - 24px - 40px);
}

.CustomerPreviewActivityPreviewProductModal__container-details {
  width: calc(50% - 12px);
  border-radius: 4px;
  overflow: auto;
  @apply tw:bg-default-000 tw:border tw:border-default-200;
}

.CustomerPreviewActivityPreviewProductModal__container-image {
  width: calc(50% - 12px);
}

.CustomerPreviewActivityPreviewProductModal__navigation {
  width: fit-content;
  margin: 0 auto;
}

.CustomerPreviewActivityPreviewProductModal_info-text {
  display: grid;
  grid-template-columns: 100px auto;
  align-items: center;
}
</style>
